import React, { useState, useEffect, useRef } from 'react';
import { FormControlLabel, Switch, Button, TextField, useTheme } from '@mui/material';
import { 
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useUnsubscribeEmail,
  NotificationPreferences 
} from '../../hooks/useNotifications';
import { useResendEmailVerification } from '../../hooks/useResendVerification';

interface EmailNotificationCardProps {
  onMessage?: (message: string) => void;
}

export const EmailNotificationCard: React.FC<EmailNotificationCardProps> = ({
  onMessage,
}) => {
  const theme = useTheme();
  const { data: preferences } = useNotificationPreferences();
  const updatePreferencesMutation = useUpdateNotificationPreferences();
  const unsubscribeEmailMutation = useUnsubscribeEmail();
  const resendEmailMutation = useResendEmailVerification();

  // Local state for form management
  const [emailChecked, setEmailChecked] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [editEmail, setEditEmail] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  
  // Ref to preserve switch state during resend verification operations
  const preserveEmailCheckedRef = useRef<boolean | null>(null);

  // Sync local state with API preferences
  useEffect(() => {
    if (preferences) {
      // Only sync switch state if we're not preserving it (during resend verification)
      if (preserveEmailCheckedRef.current === null) {
        setEmailChecked(preferences.emailSubscribed);
      } else {
        setEmailChecked(preserveEmailCheckedRef.current);
      }
      setEmail(preferences.emailAddress || '');
    }
  }, [preferences]);

  // Show verification status messages
  const getEmailStatusMessage = () => {
    if (!preferences?.emailAddress) return '';
    if (preferences.emailConfirmed) return 'Email verified ✓';
    if (preferences.emailVerificationToken) return 'Verification email sent - please check your inbox';
    return 'Email not verified';
  };

  const validateEmail = (value: string) => {
    if (!value) return 'Email is required';
    // Simple email regex
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value) ? '' : 'Invalid email address';
  };

  const handleResendEmail = async () => {
    try {
      // Preserve current switch state before mutation
      preserveEmailCheckedRef.current = emailChecked;
      await resendEmailMutation.mutateAsync();
      onMessage?.('Verification email resent successfully!');
      // Keep ref set for a bit longer to handle async refetch
      setTimeout(() => {
        preserveEmailCheckedRef.current = null;
      }, 1000);
    } catch (error: any) {
      onMessage?.(`Failed to resend email: ${error.message}`);
      preserveEmailCheckedRef.current = null; // Clear on error
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setEmailChecked(checked);
    
    if (!checked) {
      setEmailError('');
      setEditEmail(false);
      // Unsubscribe from email notifications
      unsubscribeEmailMutation.mutate(undefined, {
        onSuccess: () => {
          onMessage?.('Successfully unsubscribed from email notifications');
        },
        onError: (error: any) => {
          onMessage?.(`Failed to unsubscribe: ${error.message}`);
        }
      });
    }
  };

  const handleEmailInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPendingEmail(event.target.value);
    setEmailError(validateEmail(event.target.value));
  };

  const handleSave = () => {
    if (!editEmail) return;
    
    const err = validateEmail(pendingEmail);
    setEmailError(err);
    if (err) return;
    
    setEmail(pendingEmail);
    setEditEmail(false);
    
    // Update preferences in the database
    updatePreferencesMutation.mutate(
      {
        emailAddress: pendingEmail,
        emailSubscribed: true,
      },
      {
        onSuccess: () => {
          onMessage?.('Notification preferences updated successfully!');
        },
        onError: (error: any) => {
          onMessage?.(`Failed to update preferences: ${error.message}`);
        }
      }
    );
  };

  const isSaveDisabled = editEmail && (!!emailError || !pendingEmail);

  return (
    <>
      <FormControlLabel
        control={<Switch checked={emailChecked} onChange={handleEmailChange} />}
        label="Email Notifications"
      />
      <div style={{ marginLeft: 32, marginBottom: 16 }}>
        {emailChecked ? (
          editEmail ? (
            <>
              <TextField
                label="Email Address"
                value={pendingEmail}
                onChange={handleEmailInputChange}
                error={!!emailError}
                helperText={emailError}
                fullWidth
                margin="normal"
                type="email"
                autoComplete="email"
                sx={{ maxWidth: 320 }}
              />
              <div style={{ color: theme.palette.text.disabled, fontSize: 13, marginTop: 4 }}>
                By providing your email, you agree to receive emails from ODP.Golf. Email frequency varies. View our <a href="/privacy">privacy policy</a> for more information.
              </div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginRight: 8, marginTop: 8 }}
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                style={{ marginTop: 8 }}
                onClick={() => { setEditEmail(false); setEmailError(''); }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <span style={{ color: theme.palette.text.primary, fontSize: 15 }}>
                {email ? `Current email: ${email}` : 'No email set.'}
              </span>
              <Button
                variant="text"
                size="small"
                style={{ marginLeft: 12 }}
                onClick={() => { setEditEmail(true); setPendingEmail(email); }}
              >
                {email ? 'Change' : 'Add'}
              </Button>
              <div style={{ color: theme.palette.text.disabled, fontSize: 13, marginTop: 4 }}>
                By providing your email, you agree to receive emails from ODP.Golf. Email frequency varies. View our <a href="/privacy">privacy policy</a> for more information.
              </div>
              {getEmailStatusMessage() && (
                <div style={{ 
                  color: preferences?.emailConfirmed ? theme.palette.success.main : theme.palette.warning.main, 
                  fontSize: 13, 
                  marginTop: 8,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>{getEmailStatusMessage()}</span>
                  {preferences?.emailAddress && !preferences?.emailConfirmed && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleResendEmail}
                      disabled={resendEmailMutation.isPending}
                      style={{ fontSize: '11px', padding: '2px 8px', minWidth: 'auto' }}
                    >
                      {resendEmailMutation.isPending ? 'Sending...' : 'Send verification email'}
                    </Button>
                  )}
                </div>
              )}
            </>
          )
        ) : (
          <>
            <span style={{ color: theme.palette.text.primary, fontSize: 15 }}>
              {email ? `Current email: ${email}` : 'No email set.'}
            </span>
            {email && (
              <div style={{ color: theme.palette.text.secondary, fontSize: 12, marginTop: 6 }}>
                Email notifications are turned off. Your email remains saved; turn this back on to resume emails.
              </div>
            )}
            {!email && (
              <div style={{ color: theme.palette.text.disabled, fontSize: 13, marginTop: 4 }}>
                By providing your email, you agree to receive emails from ODP.Golf. Email frequency varies. View our <a href="/privacy">privacy policy</a> for more information.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
